import {
  UserRoleList,
  UserAuthorizationList,
  UserDataType,
  RuleResult,
  FallbackFunction,
  failureTypeEnum,
  evaluatorType,
} from "./router.types";
import { fallbackResolver } from "./router.fallback";

/**
 * Rule class for handling route access control based on user roles and authorizations.
 * Provides methods to create, combine, and evaluate access rules.
 */
export class Rule {
  private constructor(private evaluator: evaluatorType) {}

  /**
   * Evaluates the rule against user data and determines if access is allowed.
   * If access is denied, resolves the appropriate fallback route.
   *
   * @param userData - The user data to evaluate against
   * @param customFallback - Optional custom fallback function
   * @returns Result object containing allowed status and fallback information
   */
  evaluate(
    userData: UserDataType,
    customFallback?: FallbackFunction
  ): RuleResult {
    const result = this.evaluator(userData);

    if (!result.allowed) {
      // Use the fallback resolver to determine the appropriate fallback
      const fallback = fallbackResolver.resolveFallback(
        userData,
        result.failureType,
        result.failedValue,
        customFallback,
        result.isBlocked
      );

      return {
        allowed: false,
        failureType: result.failureType,
        failedValue: result.failedValue,
        fallback,
      };
    }

    return { allowed: true };
  }

  // LOGICAL OPERATORS

  /**
   * Creates a rule that requires all provided rules to pass.
   * Fails if any rule fails, returning the first failure.
   * Prioritizes authentication failure over authorization failure.
   * @param rules - Array of rules to evaluate
   * @returns A new combined rule
   */
  static all(rules: Rule[]): Rule {
    return new Rule((userData: UserDataType) => {
      let firstAuthenticationFailure: RuleResult | null = null;
      let firstAuthorizationFailure: RuleResult | null = null;

      for (const rule of rules) {
        const result = rule.evaluator(userData);
        if (!result.allowed) {
          if (
            result.failureType === failureTypeEnum.AUTHENTICATION &&
            !firstAuthenticationFailure
          ) {
            firstAuthenticationFailure = result;
          } else if (
            result.failureType === failureTypeEnum.AUTHORIZATION &&
            !firstAuthorizationFailure
          ) {
            firstAuthorizationFailure = result;
          }
        }
      }

      // Prioritize authentication failure over authorization failure
      return (
        firstAuthenticationFailure ||
        firstAuthorizationFailure || { allowed: true }
      );
    });
  }

  /**
   * Creates a rule that requires at least one of the provided rules to pass.
   * Fails only if all rules fail, returning the last failure.
   * Prioritizes authentication failure over authorization failure.
   * @param rules - Array of rules to evaluate
   * @returns A new combined rule
   */
  static any(rules: Rule[]): Rule {
    return new Rule((userData: UserDataType) => {
      let firstAuthenticationFailure: RuleResult | null = null;
      let firstAuthorizationFailure: RuleResult | null = null;

      for (const rule of rules) {
        const result = rule.evaluator(userData);
        if (result.allowed) {
          return { allowed: true };
        }

        if (
          result.failureType === failureTypeEnum.AUTHENTICATION &&
          !firstAuthenticationFailure
        ) {
          firstAuthenticationFailure = result;
        } else if (
          result.failureType === failureTypeEnum.AUTHORIZATION &&
          !firstAuthorizationFailure
        ) {
          firstAuthorizationFailure = result;
        }
      }

      // Prioritize authentication failure over authorization failure
      return (
        firstAuthenticationFailure ||
        firstAuthorizationFailure || { allowed: true }
      );
    });
  }

  // ROLE-SPECIFIC METHODS

  /**
   * Creates a rule that requires the user to have the specified role.
   *
   * @param role - The role to require
   * @returns A new role-based rule
   */
  static hasRole(role: UserRoleList): Rule {
    return Rule.hasAnyRole([role]);
  }

  /**
   * Creates a rule that requires the user to have at least one of the specified roles.
   *
   * @param roles - Array of roles, any of which would grant access
   * @returns A new role-based rule
   */
  static hasAnyRole(roles: UserRoleList[]): Rule {
    return new Rule((userData: UserDataType) => {
      const matched = roles.some((role) => userData.auth_level === role);

      if (!matched) {
        return {
          allowed: false,
          failureType: failureTypeEnum.AUTHENTICATION,
          failedValue: roles[0],
        };
      }

      return { allowed: true };
    });
  }

  /**
   * Creates a rule that blocks the user from having the specified role.
   *
   * @param role - The role to block
   * @returns A new role-based rule
   */
  static blockIfHasRole(role: UserRoleList): Rule {
    return Rule.blockIfHasAnyRole([role]);
  }

  /**
   * Creates a rule that blocks the user from having any of the specified roles.
   *
   * @param roles - Array of roles, any of which would grant access
   * @returns A new role-based rule
   */
  static blockIfHasAnyRole(roles: UserRoleList[]): Rule {
    return new Rule((userData: UserDataType) => {
      for (const role of roles) {
        if (userData.auth_level === role) {
          return {
            allowed: false,
            failureType: failureTypeEnum.AUTHENTICATION,
            failedValue: role,
            isBlocked: true,
          };
        }
      }

      return { allowed: true };
    });
  }

  // AUTHORIZATION-SPECIFIC METHODS

  /**
   * Creates a rule that requires the user to have the specified authorization.
   *
   * @param auth - The authorization to require
   * @returns A new authorization-based rule
   */
  static hasPermission(auth: UserAuthorizationList): Rule {
    return Rule.hasAllPermissions([auth]);
  }

  /**
   * Creates a rule that requires the user to have at least one of the specified authorizations.
   *
   * @param auths - Array of authorizations, any of which would grant access
   * @returns A new authorization-based rule
   */
  static hasAnyPermission(auths: UserAuthorizationList[]): Rule {
    return new Rule((userData: UserDataType) => {
      const matched = auths.some(
        (auth) => userData.authorizations?.[auth]?.achieved === true
      );

      if (!matched) {
        return {
          allowed: false,
          failureType: failureTypeEnum.AUTHORIZATION,
          failedValue: auths[0],
        };
      }

      return { allowed: true };
    });
  }

  /**
   * Creates a rule that requires the user to have all of the specified authorizations.
   *
   * @param auths - Array of authorizations, all of which are required
   * @returns A new authorization-based rule
   */
  static hasAllPermissions(auths: UserAuthorizationList[]): Rule {
    return new Rule((userData: UserDataType) => {
      for (const auth of auths) {
        if (userData.authorizations?.[auth]?.achieved !== true) {
          return {
            allowed: false,
            failureType: failureTypeEnum.AUTHORIZATION,
            failedValue: auth,
          };
        }
      }

      return { allowed: true };
    });
  }

  /**
   * Creates a rule that blocks the user from having the specified authorization.
   *
   * @param auth - The authorization to block
   * @returns A new authorization-based rule
   */
  static blockIfHasPermission(auth: UserAuthorizationList): Rule {
    return Rule.blockIfHasAllPermissions([auth]);
  }

  /**
   * Creates a rule that blocks the user from having any of the specified authorizations.
   *
   * @param auths - Array of authorizations, any of which would grant access
   * @returns A new authorization-based rule
   */
  static blockIfHasAnyPermission(auths: UserAuthorizationList[]): Rule {
    return new Rule((userData: UserDataType) => {
      for (const auth of auths) {
        if (userData.authorizations?.[auth]?.achieved === true) {
          return {
            allowed: false,
            failureType: failureTypeEnum.AUTHORIZATION,
            failedValue: auth,
            isBlocked: true,
          };
        }
      }

      return { allowed: true };
    });
  }

  /**
   * Creates a rule that blocks the user from having all of the specified authorizations.
   *
   * @param auths - Array of authorizations, all of which are required
   * @returns A new authorization-based rule
   */
  static blockIfHasAllPermissions(auths: UserAuthorizationList[]): Rule {
    return new Rule((userData: UserDataType) => {
      const hasAll = auths.every(
        (auth) => userData.authorizations?.[auth]?.achieved === true
      );

      if (hasAll) {
        return {
          allowed: false,
          failureType: failureTypeEnum.AUTHORIZATION,
          failedValue: auths[0],
          isBlocked: true,
        };
      }

      return { allowed: true };
    });
  }

  /**
   * Creates a rule that allows public access (always returns allowed).
   *
   * @returns A new rule that always grants access
   */
  static public(): Rule {
    return new Rule(() => ({ allowed: true }));
  }
}
